import React, { useState } from 'react';
import { Container, Row, Col, Card, Accordion, Button } from 'react-bootstrap';
import { 
  FaQuestionCircle, 
  FaChevronDown, 
  FaChevronUp, 
  FaEnvelope, 
  FaSearch,
  FaShieldAlt,
  FaExclamationTriangle,
  FaEdit,
  FaLock,
  FaInfoCircle,
  FaHeadset
} from 'react-icons/fa';
import { BsQuestionCircleFill } from 'react-icons/bs';
import Layout from '../components/layout/Layout';
import './LegalPages.css';

const faqItems = [
  {
    question: 'How do I create an account?',
    answer: 'To create an account, click on the "Sign Up" button in the top right corner of the page and follow the registration process.'
  },
  {
    question: 'How do I reset my password?',
    answer: 'Click on "Forgot Password" on the login page and follow the instructions sent to your registered email address.'
  },
  {
    question: 'Is my personal information secure?',
    answer: 'Yes, we take your privacy and security seriously. All personal information is encrypted and stored securely.'
  },
  {
    question: 'How can I contact support?',
    answer: 'You can reach our support team 24/7 through the contact form on our website or by emailing support@unitrade.com.'
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards, PayPal, and bank transfers.'
  },
  {
    question: 'How do I report a problem with the platform?',
    answer: 'Please use the "Report an Issue" feature in your account settings or contact our support team directly.'
  },
  {
    question: 'Can I edit or delete my account?',
    answer: 'Yes, you can edit your account details in the settings section. To delete your account, please contact our support team.'
  },
  {
    question: 'What is your refund policy?',
    answer: 'We offer a 30-day money-back guarantee on all our plans. Please refer to our Terms of Service for more details.'
  },
  {
    question: 'How do I update my profile information?',
    answer: 'You can update your profile information at any time by going to "My Account" and selecting "Edit Profile".'
  },
  {
    question: 'Are there any prohibited items on the platform?',
    answer: 'Yes, please refer to our Terms of Service for a complete list of prohibited items and activities on our platform.'
  }
];

const FAQ = () => {
  const [activeKey, setActiveKey] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter FAQ items based on search term
  const filteredFaqItems = faqItems.filter(item => 
    item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get icon based on question content
  const getQuestionIcon = (question) => {
    const lowerQuestion = question.toLowerCase();
    
    if (lowerQuestion.includes('safe') || lowerQuestion.includes('secure')) {
      return <FaShieldAlt className="me-2 text-success" />;
    } else if (lowerQuestion.includes('account') || lowerQuestion.includes('password')) {
      return <FaLock className="me-2 text-warning" />;
    } else if (lowerQuestion.includes('report') || lowerQuestion.includes('prohibited')) {
      return <FaExclamationTriangle className="me-2 text-danger" />;
    } else if (lowerQuestion.includes('edit') || lowerQuestion.includes('delete')) {
      return <FaEdit className="me-2 text-info" />;
    } else if (lowerQuestion.includes('contact') || lowerQuestion.includes('support')) {
      return <FaEnvelope className="me-2 text-primary" />;
    } else if (lowerQuestion.includes('privacy') || lowerQuestion.includes('personal')) {
      return <FaShieldAlt className="me-2 text-purple" />;
    }
    
    return <FaQuestionCircle className="me-2 text-primary" />;
  };

  const toggleAccordion = (key) => {
    setActiveKey(activeKey === key ? null : key);
  };

  return (
    <Layout>
      <Container className="legal-page">
        <Row className="justify-content-center">
          <Col lg={10}>
            <div className="text-center mb-5">
              <BsQuestionCircleFill size={48} className="text-primary mb-3" />
              <h1 className="page-title">Frequently Asked Questions</h1>
              <p className="last-updated">
                <FaInfoCircle className="me-2" />
                Last Updated: July 20, 2025
              </p>
            </div>
            
            <section className="mb-5">
              <div className="search-box mb-5 p-4 bg-light rounded-3">
                <div className="d-flex align-items-center">
                  <div className="search-icon me-3">
                    <FaSearch size={24} className="text-muted" />
                  </div>
                  <div className="flex-grow-1">
                    <input 
                      type="text" 
                      className="form-control form-control-lg" 
                      placeholder="Search our FAQs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <p className="text-center mt-3 mb-0">
                  Can't find what you're looking for? Contact our <a href="/contact" className="text-primary fw-bold">support team</a> for assistance.
                </p>
              </div>
              
              <Accordion activeKey={activeKey} className="faq-accordion">
                {filteredFaqItems.map((item, index) => {
                  // Get appropriate icon for the question
                  const icon = getQuestionIcon(item.question);
                  
                  return (
                    <Card key={index} className="mb-3">
                    <Card.Header 
                      onClick={() => toggleAccordion(index.toString())}
                      className={`faq-question ${activeKey === index.toString() ? 'active' : ''}`}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="d-flex align-items-center">
                          {icon}
                          {item.question}
                        </span>
                        {activeKey === index.toString() ? <FaChevronUp /> : <FaChevronDown />}
                      </div>
                    </Card.Header>
                    <Accordion.Collapse eventKey={index.toString()}>
                      <Card.Body>
                        <p className="mb-0">{item.answer}</p>
                      </Card.Body>
                    </Accordion.Collapse>
                    </Card>
                  );
                })}
              </Accordion>
              
              {filteredFaqItems.length === 0 && (
                <div className="text-center py-5">
                  <FaSearch size={48} className="text-muted mb-3" />
                  <h4>No results found</h4>
                  <p className="text-muted">Try different search terms or contact our support team for assistance.</p>
                  <Button 
                    variant="outline-primary" 
                    className="mt-3"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear Search
                  </Button>
                </div>
              )}
            </section>
            
            <section className="mt-5 text-center p-4 bg-light rounded-3">
              <div className="icon-wrapper mb-3">
                <FaHeadset size={40} className="text-primary" />
              </div>
              <h3 className="section-title mb-4">Still have questions?</h3>
              <p className="mb-4">
                If you can't find the answer to your question, our support team is here to help!
                We're available 24/7 to assist you with any questions or concerns.
              </p>
              <div className="d-flex gap-3 justify-content-center">
                <Button href="/contact" variant="primary" className="px-4">
                  <FaEnvelope className="me-2" /> Email Support
                </Button>
                <Button href="/contact" variant="outline-primary" className="px-4">
                  <FaHeadset className="me-2" /> Live Chat
                </Button>
              </div>
            </section>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default FAQ;
